import { useState, useRef, useEffect } from "react";
import { useParams } from "react-router-dom";
//import { toast } from "react-toastify";

import {
  Box,
  Collapse,
  FormControlLabel,
  FormControl,
  InputAdornment,
  IconButton,
  OutlinedInput,
  Switch,
  Slide,
  Typography,
  TextField,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import styled from "styled-components";

import SearchIcon from "@mui/icons-material/Search"; // MUI의 검색 아이콘
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';

import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

import { 
  useGetTableData, useGetTableDataInQueryString,
  usePutTableData, 
  useCreateTableData, 
  useDeleteTableData 
} from "../../../components/Table/hooks";

import { 
  registerMachineColumns, registerMachineColumns_edit, registerMachineColumns_admin, 
  standardProcessColumns,
} from "../../../constants/columns";

import { NewTableTemplate } from "../../../components/Table/TableTemplate";
import { ModalTemplate } from "../../../components/Table/ModalTemplate";
import Buttons from "../../../../common/Buttons";
import { 
  BreadcrumbsTable,
  ConfirmDialog, 
  deepCopy, 
  normalizeToArray,
  SnackbarAlert,
  formatDateWithDayjs, formatDateToDayjs,
} from "../../../components/common/HelperFunc";
import { AutoPilotGuide } from "../../../components/common/AutoPilotGuide";
import * as message from "../../../constants/messages";
import { FONT_PROPS } from "../../../styles/colors";

///////////////////////////////
// 스타일
///////////////////////////////
// 테이블 컨테이너 스타일
const TableContainer = styled.div`
  width: 100%;
  height: 100%;
  padding: 16px 32px 16px 32px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

//////////////////////////////////
// 공정과 기계/기구 매칭 컴포넌트
//////////////////////////////////
const PARENT_TITLE = "등록관리";
const CHILD_TITLE = "공정과 기계/기구 매칭";
const REGISTER_MACHINE_AUTOPILOT_STEPS = [
  {
    target: '[data-autopilot="register-machine-actions"]',
    title: "설비 중심 연결 시작",
    description:
      "이 화면은 설비를 먼저 선택하고, 선택한 설비에 연결할 공정을 붙이는 흐름입니다. 안내 중에는 데이터를 저장하지 않습니다.",
  },
  {
    target: '[data-autopilot="register-machine-list"]',
    title: "설비 선택",
    description:
      "왼쪽 또는 전체 설비 목록에서 공정을 연결할 설비를 고릅니다. 선택한 설비 기준으로 연결 상태를 확인합니다.",
  },
  {
    target: '[data-autopilot="register-process-list"]',
    title: "공정 선택",
    description:
      "설비 편집 화면에서 대상 공정 목록이 열리면 연결할 공정을 선택합니다. 이미 연결된 공정은 중복 등록되지 않습니다.",
  },
  {
    target: '[data-autopilot="register-machine-actions"]',
    title: "연결 저장",
    description:
      "공정 선택 후 추가 버튼으로 설비-공정 연결을 저장합니다. 시연에서는 버튼 위치와 흐름만 확인하면 됩니다.",
  },
];

export function RegisterMachine() {
  const { group_code, project_code } = useParams();
  const theme = useTheme();
  const isReadOnly = useMediaQuery(theme.breakpoints.down("md"));

  const UPDATE_STATUS = Object.freeze({ // 수정 상태
    EDIT: 'edit',
    DELETE: 'delete',
  });

  const FILTER_STATUS = Object.freeze({ // 필터 상태
    ALL: "all",
    REGISTERED: "registered",
  });

  ////////////////////////
  // CRUD 요청
  ////////////////////////

  // 데이터 조회에 필요한 조건을 상태로 관리합니다.
  const [searchStatus, setSearchStatus] = useState({ filter: FILTER_STATUS.REGISTERED });
  const [autopilotOpen, setAutopilotOpen] = useState(false);
  const [autopilotStepIndex, setAutopilotStepIndex] = useState(0);
  // 데이터 조회에 필요한 쿼리스트링을 만듭니다.
  const qeuryString = { 
    params: { // 쿼리스트링 만들때 params 이름을 사용하는게 규칙입니다.
      filter: searchStatus.filter,
    },
  }

  // 데이터 조회 요청
  const getData = useGetTableDataInQueryString(
    "request",
    "/getRegisterAsset", 
    group_code,
    project_code,
    qeuryString,
  );
  // 데이터 생성 요청
  const postData = useCreateTableData( 
      "request",
      "/postRegisterAsset",
      group_code,
      project_code
  );
  // 데이터 수정 요청
  const putData = usePutTableData(
    "request",
    "/putRegisterAsset",
    group_code,
    project_code
  );
  // 데이터 삭제 요청
  const deleteData = useDeleteTableData(
    "request",
    "/deleteRegister",
    group_code,
    project_code
  );

  // 공정등록 데이터 조회 요청
  const getProcessData = useGetTableDataInQueryString(
    "request",
    "/getSafeProcess", // 서버 요청 경로
    group_code,
    project_code,
    qeuryString,
    //{ params: { filter: "registered" } }, // 유지 항목만 조회
  );
  // 대상 기계/기구 정보 데이터 조회 요청
  const getStandardMachineData = useGetTableData(
    "request",
    "/getStandardMachine",
    group_code,
    project_code
  );

  /**
   * tempData 데이터 구조
   *  [
   *    {
          machineData: { machine_id: 1, ... },
          processData:
          [
            { process_id:1, ... }, 
            { process_id:2, ... }, 
             ...
          ]
        },
        ...
      ]
  */

  // 데이터 가공 (삭제된 항목은 배경색 변경)
  //const [tempBackupData, setTempBackupData] = useState([]); // 원본 데이터
  const [tempData, setTempData] = useState([]);
  useEffect(() => {
    if (!getStandardMachineData.data || !getData.data) return;

    const originalRowData = deepCopy(getData.data);

    // 1. originalRowData를 machine_id 기준으로 그룹핑
    const processMap = originalRowData.reduce((acc, cur) => {
      const { machine_id,
        register_asset_id, register_asset_create_date, register_asset_delete_date, register_asset_status,
        process_id, process_name, process_description, process_index, process_create_date, process_delete_date, process_status, 
      } = cur;
      if (!acc[machine_id]) acc[machine_id] = [];
      acc[machine_id].push({
        register_asset_id,
        register_asset_create_date,
        register_asset_delete_date,
        register_asset_status,
        process_id,
        process_name,
        process_description,
        process_index,
        process_create_date,
        process_delete_date,
        process_status,
      });
      return acc;
    }, {});
    
    // 2. getStandardMachineData.data를 기준으로 전체 machineData 유지
    const grouped = getStandardMachineData.data.map(machine => ({
      machineData: {
        ...machine,
      },
      // originalData에 해당 machine_id 있으면 processData 채우기
      processData: processMap[machine.machine_id] || [],
      __bgColor: theme.palette.background.paper,
    }));

    // 원본 / 편집 데이터 보관
    //setTempBackupData(deepCopy(grouped));
    setTempData(deepCopy(grouped));
  }, [getData.data, getStandardMachineData.data, theme.palette.background.paper]);

  // 데이터 업데이트 알람 상태 관리
  const [validationAlert, setValidationAlert] = useState({
    isOpen: false,
    message: '',
    severity:'' // 'success' | 'error' | 'warning' | 'info'
  });
  const openValidationAlert = (message, severity = 'success') => {
    setValidationAlert({
      ...validationAlert,
      isOpen: true,
      message: message,
      severity: severity
    });
  };
  const closeValidationAlert = () => {
    setValidationAlert({
      ...validationAlert,
      isOpen: false,
      message: '',
      severity: '',
    });
  };

  // 현재 렌더링 상태 관리 
  const RenderState = Object.freeze({
    VIEW: "viewMode", // 조회 모드
    EDIT: "editMode", // 편집 모드
    ADMIN: "adminMode", // 관리자 모드
  });
  const [mode, setMode] = useState(RenderState.VIEW);
  const changeModeState = (newState) => setMode(newState);

  // 선택한 설비 항목 관리
  const [selectedEditItems, setSelectedEditItems] = useState({ machineIds: [] });
  const setEditItemSelect = ({ machineIds }) => setSelectedEditItems({ machineIds });
  const clearEditItemSelect = () => setSelectedEditItems({ machineIds: [] });

  // 위험성평가 설비 등록 테이블 참조
  const registerMachineTableRef = useRef();
  const clearRegisterMachineSelection = () => { registerMachineTableRef.current?.clearSelection(); };
  const getRegisterMachineSelectedRows = () => { return registerMachineTableRef.current?.getSelectedRows(); };

  // 위험성평가 설비 등록 테이블의 상단 툴바 커스터마이징 관련 내용
  const [showSearch, setShowSearch] = useState(false); // 검색창 열림 여부
  const [globalFilter, setGlobalFilter] = useState(''); // 검색 텍스트 상태
  const customRegisterMachineTableTopToolbar = (table) => {
    const data = table.getSelectedRowModel().rows.map(row => row.original);
    const hasData = data.filter(item => {
      return Array.isArray(item.processData) && item.processData.length > 0;
    });
    const isDeleteDisabled = hasData.length === 0;

    return (
      <Box width="100%" display="flex" justifyContent="space-between" alignItems="center" p={2}>
        {/* 왼쪽 영역 */}
        <Box display="flex" alignItems="center" gap={2}>
          {/* TODO: */}
        </Box>

        {/* 오른쪽 영역 - 기존 내부 액션도 원하면 여기에 직접 렌더링 */}
        <Box display="flex" alignItems="center" gap={2}>
          {/* 검색창 */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton onClick={() => setShowSearch((prev) => !prev)}>
              <SearchIcon />
            </IconButton>
          
            <Collapse in={showSearch} orientation="horizontal">
              <TextField
                size="small"
                placeholder="검색"
                value={globalFilter}
                onChange={(e) => {
                  setGlobalFilter(e.target.value);
                  table.setGlobalFilter(e.target.value);
                }}
                sx={{ minWidth: 200 }}
                variant="outlined"
                slots={{
                  input: OutlinedInput,
                }}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => {
                          setGlobalFilter('');
                          table.setGlobalFilter('');
                          setShowSearch(false); // 닫기
                          }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }
                }}
              />
            </Collapse>
          </Box>

          {/* 편집 */}
          {!isReadOnly && mode === RenderState.VIEW &&
            <Buttons.Custom
              onClick={() => {
                changeModeState(RenderState.EDIT);
                //openEditModal();
              }}
              startIcon={<EditIcon />}
              sx={{ padding: '12px 16px' }}
            >
              편집
            </Buttons.Custom>
          }
          
          {/* 삭제 버튼 */}
          {!isReadOnly && mode === RenderState.EDIT &&
            <Buttons.Delete
              onClick={() => {
                openDeleteModal(data);
              }}
              disabled={isDeleteDisabled}
              sx={{ padding: '12px 16px' }}
            />
          }
          
          {/* 데이터 삭제 버튼 */}
          {!isReadOnly && mode === RenderState.ADMIN && 
            <Buttons.Delete 
              onClick={() => {
                openDeleteRecordModal(data);
              }}
              disabled={isDeleteDisabled}
              sx={{ padding: '12px 16px',}}
            >
              데이터 삭제
            </Buttons.Delete>
          }
          {/* 관리자 모드 */}
          {!isReadOnly && <FormControl component="fieldset">
            <FormControlLabel
              value="top"
              control={
                <Switch color="primary" 
                  checked={mode === RenderState.ADMIN}
                  //checked={renderState === "adminMode" ? true : false}
                  onChange={(event) => {
                    const state = event.target.checked ? RenderState.ADMIN : RenderState.VIEW;
                    changeModeState(state);

                    setSearchStatus({ ...searchStatus, filter: event.target.checked ? FILTER_STATUS.ALL : FILTER_STATUS.REGISTERED});
                    clearEditItemSelect();
                    clearRegisterMachineSelection();
                    clearProcessSelection();
                  }}
                />
              }
              label="관리자모드"
              labelPlacement="end" // bottom,end,start,top
              sx={{marginLeft : '4px'}}
            />
          </FormControl>}       
        </Box>
      </Box>
    );
  };

  // 대상 공정 선택 테이블 참조
  const processTableRef = useRef();
  const clearProcessSelection = () => { processTableRef.current?.clearSelection(); };
  const getProcessSelectedRows = () => { return processTableRef.current?.getSelectedRows(); };

  // 대상 공정 선택 테이블의 상단 툴바 커스터마이징 관련 내용
  const [showProcessSearch, setShowProcessSearch] = useState(false); // 검색창 열림 여부
  const [processGlobalFilter, setProcessGlobalFilter] = useState(''); // 검색 텍스트 상태
  const customProcessTableTopToolbar = (table) => (
    <Box width="100%" display="flex" justifyContent="space-between" alignItems="center" p={2}>
      {/* 왼쪽 영역 */}
      <Box display="flex" alignItems="center" gap={2}>
        {/* TODO: */}
      </Box>

      {/* 오른쪽 영역 - 기존 내부 액션도 원하면 여기에 직접 렌더링 */}
      <Box display="flex" alignItems="center" gap={2}>
        {/* 검색창 */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={() => setShowProcessSearch((prev) => !prev)}>
            <SearchIcon />
          </IconButton>
        
          <Collapse in={showProcessSearch} orientation="horizontal">
            <TextField
              size="small"
              placeholder="검색"
              value={processGlobalFilter}
              onChange={(e) => {
                setProcessGlobalFilter(e.target.value);
                table.setGlobalFilter(e.target.value);
              }}
              sx={{ minWidth: 200 }}
              variant="outlined"
              slots={{
                input: OutlinedInput,
              }}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => {
                          setProcessGlobalFilter('');
                          table.setGlobalFilter('');
                          setShowProcessSearch(false); // 닫기
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }
              }}
            />
          </Collapse>
        </Box>

        {/** 추가 버튼 */}
        {/* <Buttons.Custom
          onClick={() => {
            addProcess({ machineIds: selectedEditItems.machineIds });
          }}
          disabled={selectedEditItems.machineIds.length === 0}
          startIcon={<KeyboardDoubleArrowLeftIcon />}
          sx={{ padding: '12px 16px' }}
        >
          추가
        </Buttons.Custom> */}

        {/** 닫기 버튼 */}
        {/* <Buttons.Custom
            onClick={() => {
              changeModeState(RenderState.VIEW);
              clearEditItemSelect();
              clearRegisterMachineSelection();
              clearMachineSelection();
            }}
            startIcon={<CloseIcon />}
            sx={{ padding: '12px 16px' }}
          >
            닫기
        </Buttons.Custom> */}
      </Box>
    </Box>
  );
  // 대상 공정 선택 테이블의 하단 툴바 커스터마이징 관련 내용
  const customProcessTableBottomToolbar = (table) => {
    const allRows = table.getRowModel().rows.length;
    const selectedRows = table.getSelectedRowModel().rows.length;
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          p: 1.5,
          borderTop: `1px solid ${theme.palette.divider}`,
          bgcolor: theme.palette.background.paper,
        }}
      >
        {/** 왼쪽 영역 */}
        <Box sx={{ display: 'flex', flex: 1, alignItems: 'center' }}>
          {/** 선택 초기화 */}
          {selectedRows > 0 && 
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 4, px: 6, py: 1 }}>
              <Typography variant="h6" component="div" 
                sx={{ fontWeight: FONT_PROPS.REGULAR, fontSize: 14, color: theme.palette.text }}
              >
                {allRows} 행 중 {selectedRows} 행 선택됨
              </Typography>
              <Typography
                onClick={() => clearProcessSelection()}
                sx={{
                  color: theme.palette.primary.main,
                  fontWeight: FONT_PROPS.MEDIUM,
                  fontSize: 13,
                  cursor: 'pointer',
                  '&:hover': { 
                    color: theme.palette.primary.dark,
                    backgroundColor: theme.palette.primary.lighterOpacity,
                   },
                }}
              >
                선택 초기화
              </Typography>
            </Box>
          }
        </Box>

        {/** 오른쪽 영역 */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          {/** 추가 버튼 */}
          <Buttons.Custom
            onClick={() => {
              addProcess({ machineIds: selectedEditItems.machineIds });
            }}
            disabled={selectedEditItems.machineIds.length === 0}
            startIcon={<KeyboardDoubleArrowLeftIcon />}
            sx={{ padding: '12px 16px' }}
          >
            추가
          </Buttons.Custom>

          {/** 닫기 버튼 */}
          <Buttons.Custom
              onClick={() => {
                changeModeState(RenderState.VIEW);
                clearEditItemSelect();
                clearRegisterMachineSelection();
                clearProcessSelection();
              }}
              startIcon={<CloseIcon />}
              sx={{ padding: '12px 16px' }}
            >
              닫기
          </Buttons.Custom>
        </Box>
      </Box>
    );
  };
  // 대상 공정 추가
  const addProcess = ({ machineIds }) => {
    // 선택한 항목
    const selectedProcesses = getProcessSelectedRows().map((row) => row.original);

    // 선택 체크
    if (selectedProcesses.length === 0) {
      openValidationAlert("추가할 공정을 선택해주세요.", "error");
      return;
    }

    // 중복 체크 및 추가할 데이터 구성
    const updateData = tempData.reduce((acc, item) => {
      if (!machineIds.includes(item.machineData.machine_id)) return acc;

      // 새로 추가된 process 추출
      const newProcesses = selectedProcesses.filter(
        (process) => !item.processData.some((registeredProcess) => registeredProcess.process_id === process.process_id)
      );

      if (newProcesses.length > 0) {
        acc.push({
          machineData: item.machineData,
          processData: newProcesses.map((process) => ({
            process_id: process.process_id,
            process_name: process.process_name,
            process_description: process.process_description,
          })),
        });
      }
      return acc;
    }, []);

    // 추가된 항목이 있다면 업데이트
    if (updateData.length > 0) {
      addRegisterAssetData(updateData);
    } else {
      openValidationAlert("이미 연결된 공정입니다.", "warning");
    }

    // ✅ 중복이 있든 없든 항상 선택 해제
    clearProcessSelection();
  };
  // DB 업데이트(추가 데이터)
  const addRegisterAssetData = async (updateData) => {
    try {
      //console.log("편집 데이터: ", updateData);

      // 데이터베이스 업데이트
      const result = await postData.mutateAsync(updateData);
      if(result.status === 200) {
        openValidationAlert(message.addSuccess);
      }

      // 생성 모달 초기화
      //closeEditModal();
    }
    catch (error) {
      console.error("데이터 추가 중 오류 발생:", error);  
    }
  };

  const getRegisterAssetRefs = (items) =>
    normalizeToArray(items).flatMap((item) => {
      const processData = Array.isArray(item?.processData) ? item.processData : [];
      const machineData = Array.isArray(item?.machineData) ? item.machineData : [];
      return [...processData, ...machineData]
        .filter((data) => data?.register_asset_id != null)
        .map((data) => ({ register_asset_id: data.register_asset_id }));
    });

  // 공정등록 수정 모달 상태 관리
  const [editModal, setEditModal] = useState({ isOpen: false, rowData: [] });
  const openEditModal = (rowData) => setEditModal({ isOpen: true, rowData: rowData});
  const closeEditModal = () => setEditModal({ isOpen: false, rowData: [] });
  // 공정등록 테이블의 행 수정 액션
  const customRenderRowActionContent = (rowData) => {
    return(
      <Box sx={{ width: 80, }}>
        <Buttons.Edit
          onClick={(event) =>{
            event.stopPropagation(); // 이벤트 전파 중단
            openEditModal(rowData);
          }}
          sx={{ padding: '8px 12px' }}
        />
      </Box>
    );
  };
  // DB 업데이트 (수정 데이터)
  const updateEditData = async (updateData) => {
    //console.log(updateData);
    try {

      const data ={
        updateData: updateData,
        updateStatus: UPDATE_STATUS.EDIT,
      };

      const result = await putData.mutateAsync(data);
      if(result.status === 200) {
        openValidationAlert(message.updateEditSuccess);
      }

      // 수정 모달 초기화
      closeEditModal();
    }
    catch (error) {
        console.error("데이터 수정 중 오류 발생:", error);
    }
  }

  // 위험성평가 설비 등록 삭제 모달 상태 관리
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, deleteItem: null });
  const openDeleteModal = (item) => setDeleteModal({ isOpen: true, deleteItem: item });
  const closeDeleteModal = () => setDeleteModal({ isOpen: false, deleteItem: null });

  // DB 업데이트 (논리 삭제 데이터)
  const softDeleteRegisterAssetData = async () => {
    closeDeleteModal();
    //console.log('✅ 확인 눌림 → 다음 동작 실행');
    try {

      const data = {
        updateData: [{ machineData: getRegisterAssetRefs(deleteModal.deleteItem) }],
        updateStatus: UPDATE_STATUS.DELETE,
      };

      // 데이터베이스 업데이트
      const result = await putData.mutateAsync(data);
      if(result.status === 200) {
        openValidationAlert(message.deleteSuccess);
      }

      // 선택 상태 초기화
      clearRegisterMachineSelection();
      
    }
    catch (error) {
      console.error("데이터 삭제 중 오류 발생:", error);
    }
  };

  // 물리 삭제 모달 상태 관리
  const [deleteRecordModal, setDeleteRecordModal] = useState({ isOpen: false, deleteItem: null });
  const openDeleteRecordModal = (item) => setDeleteRecordModal({ isOpen: true, deleteItem: item });
  const closeDeleteRecordModal = () => setDeleteRecordModal({ isOpen: false, deleteItem: null });

  // DB 업데이트 (물리 삭제 데이터)
  const deleteRegisterAssetData = async () => {
    closeDeleteRecordModal();
    //console.log('✅ 확인 눌림 → 다음 동작 실행');
    try {
      const data = getRegisterAssetRefs(deleteRecordModal.deleteItem);

      // 데이터베이스 업데이트
      const result = await deleteData.mutateAsync({
        deleteData: data
      });

      if(result.status === 200) {
        openValidationAlert(message.deleteSuccess);
      }

      // 선택 상태 초기화
      clearRegisterMachineSelection();
    }
    catch (error) {
      console.error("데이터 삭제 중 오류 발생:", error);
    }
  };

  // 렌더링할 컬럼 설정
  const getColums = () => {
    if(mode === RenderState.EDIT) return registerMachineColumns_edit;
    else if(mode === RenderState.ADMIN) return registerMachineColumns_admin;
    else return registerMachineColumns;
  }

  ////////////////////////
  // 렌더링
  ////////////////////////
  function renderData() {
    //console.log(renderState);
    return (
      <TableContainer>
        <Box data-autopilot="register-machine-actions" sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
          <Tooltip title="오토파일럿">
            <IconButton
              color="primary"
              onClick={() => {
                setAutopilotStepIndex(0);
                setAutopilotOpen(true);
              }}
              sx={{
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
              }}
            >
              <PlayCircleOutlineIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <Box sx={{ display: "flex", flex: 1, width: '100%', height: '100%', overflow: 'hidden', gap: 2 }}>
          {/** 위험성평가 설비 등록 테이블 */}
          <Box data-autopilot="register-machine-list" sx={{ display: "flex", flexDirection: 'column', width: (mode === RenderState.EDIT) ? '40%' : '100%', border: `1px solid ${theme.palette.divider}`, borderRadius: "0.375rem", boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.05)' }}>
            <NewTableTemplate 
              ref={registerMachineTableRef}
              columns={getColums()}
              data={tempData}
              topToolbarContent={customRegisterMachineTableTopToolbar}
              renderRowActionContent={isReadOnly ? null : customRenderRowActionContent}
              onSelectedRowsChange={(rowIds) => {
                const ids = getRegisterMachineSelectedRows().map(id => id.original.machineData.machine_id);
                setEditItemSelect({ machineIds: ids });
              }}
              enabledSelectionRow={!isReadOnly && mode !== RenderState.VIEW}
              enableCustomGlobalFilter={true}
              enableEditing={!isReadOnly && mode === RenderState.ADMIN}
              editDisplayMode={'row'}
              renderRowActionCustomOptions={({ values, row, table }) => {
                const normalizedValues = normalizeToArray(values);
                if (isReadOnly) return;
                if(mode === RenderState.EDIT) openDeleteModal(normalizedValues);
                else openDeleteRecordModal(normalizedValues);
              }}
              // templateSX={{
              //   border: `1px solid ${theme.palette.divider}`,
              //   borderRadius: "0.375rem", 
              //   boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.05)',
              // }}
            />
          </Box>

          {/** 대상 공정 정보 테이블 */}
          <Slide direction="left" in={!isReadOnly && mode === RenderState.EDIT} mountOnEnter unmountOnExit>
            <Box data-autopilot="register-process-list" sx={{ display: "flex", flexDirection: 'column', width: '60%', border: `1px solid ${theme.palette.divider}`, borderRadius: "0.375rem", boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.05)' }}>
              {/** 제목 표시 */}
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: "center", width: '100%', height: '35px', bgcolor: theme.palette.primary.lighterOpacity, padding: '8px',}}>
                <Box sx={{ display: "flex", flex: 1, justifyContent: 'center', alignItems: "center", }}>
                  <Typography sx={{ fontWeight: FONT_PROPS.MEDIUM, fontSize: 15, color: theme.palette.primary.main }}>
                    대상 공정 정보
                  </Typography>
                </Box>
              </Box>
              {/** 테이블 표시 */}
              <Box sx={{ flex: 1, overflow: 'hidden' }}>
                <NewTableTemplate 
                  ref={processTableRef}
                  columns={standardProcessColumns}
                  data={getProcessData.data || []}
                  topToolbarContent={customProcessTableTopToolbar}
                  bottomToolbarContent={customProcessTableBottomToolbar}
                  //renderRowActionContent={customRenderRowActionContent}
                  enableEditing={false}
                  enableCustomGlobalFilter={true}
                  //enableCustomBottomToolbar={true}
                  // templateSX={{
                  //   border: `1px solid ${theme.palette.divider}`,
                  //   borderRadius: "0.375rem", 
                  //   boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.05)',
                  // }}
                />
              </Box>
            </Box>
          </Slide>
        </Box>
        {/* 행(row) 수정 모달 */}
        <EditRowModalAdmin
          isOpen={editModal.isOpen}
          originalRowData={editModal.rowData}
          onConfirm={updateEditData}
          onCancel={closeEditModal}
        />
        {/* 논리 삭제 메시지 */}
        <ConfirmDialog
          open={deleteModal.isOpen}
          title="정말 삭제하시겠어요?"
          message="삭제하면 복구할 수 없습니다."
          onConfirm={softDeleteRegisterAssetData}
          onCancel={closeDeleteModal}
        />
        {/** 물리 삭제 메시지 */}
        <ConfirmDialog
          open={deleteRecordModal.isOpen}
          title="정말 삭제하시겠어요?"
          message="삭제하면 복구할 수 없습니다."
          onConfirm={deleteRegisterAssetData}
          onCancel={closeDeleteRecordModal}
        />
        {/* 데이터 알람 */}
        <SnackbarAlert
          open={validationAlert.isOpen}
          onClose={closeValidationAlert}
          message={validationAlert.message}
          severity={validationAlert.severity} // 'success' | 'error' | 'warning' | 'info'
          //anchorOrigin = {{vertical: "top", horizontal: "center"}}
        />
        <AutoPilotGuide
          open={autopilotOpen}
          steps={REGISTER_MACHINE_AUTOPILOT_STEPS}
          stepIndex={autopilotStepIndex}
          onStepChange={setAutopilotStepIndex}
          onClose={() => setAutopilotOpen(false)}
        />
      </TableContainer>
    );
  }

  // RegisterMachine 렌더링
  return (
    <Box sx={{ bgcolor: 'var(--mui-palette-background-paper)', height: '100%', display: 'flex', flexDirection: 'column'}}>
      {/* 브레드크럼 + 타이틀 */}
      <BreadcrumbsTable parentTitle={PARENT_TITLE} childTitle={CHILD_TITLE} />
      {/* 테이블 표시 */}
      {renderData()}
    </Box>
  );
}

// 테이블의 행(row) 수정 모달 - 관리자 모드
const EditRowModalAdmin = ({ isOpen, originalRowData, onConfirm, onCancel }) => {
  const theme = useTheme();

  // 유효성 검사 알람 상태 관리
  const [validationAlert, setValidationAlert] = useState({
    isOpen: false,
    message: '',
    severity: '',
  });
  const openValidationAlert = (message, severity = 'error') => {
    setValidationAlert({
      ...validationAlert,
      isOpen: true,
      message: message,
      severity: severity,
    });
  };
  const closeValidationAlert = (event, reason) => {
    if (reason === 'clickaway') return;
    setValidationAlert({
      ...validationAlert,
      isOpen: false,
      message: '',
      severity: '',
    });
  };

  // 수정 행 데이터 관리
  const [editData, setEditData] = useState([]);
  const clearEditData = () => setEditData([]);
  useEffect(() =>{
    if(!originalRowData || originalRowData.length === 0) {
      return;
    }

    const tempData = {
      ...originalRowData,
      processData: originalRowData.processData.map((process) => ({
        ...process,
        // 여기서 원하는 값으로 변경
        register_asset_create_date: formatDateToDayjs(process.register_asset_create_date),
        register_asset_delete_date: formatDateToDayjs(process.register_asset_delete_date),
      })),
    };

    setEditData(tempData);
  },[originalRowData])

  // 행 수정 완료 액션
  const editAction = () => {
    if(checkEmptyData(editData)) {
      clearEditData();
      onCancel();
      return;
    } 
    if(validateData(editData)) {
      openValidationAlert(message.notAllowedEmptyRegisterDate);
      return;
    };

    //console.log("수정된 데이터:", editData);

    // 데이터 포맷 변경
    const data = { 
      ...editData, 
      // 변경할 내용
      machineData: editData.processData.map((process) => ({
        ...process,
        register_asset_create_date: formatDateWithDayjs(process.register_asset_create_date),
        register_asset_delete_date: formatDateWithDayjs(process.register_asset_delete_date),
        register_asset_status: process.register_asset_delete_date ? 1 : 0, // 삭제일자가 있으면 상태 1(삭제), 없으면 0(정상)
      })),
    };

    onConfirm(data);
    clearEditData();
  };

  // 데이터 빈칸 체크
  const checkEmptyData = (data) => {
    if(data.processData.length === 0) { 
      return true;
    }
    return false;
  };
  // 데이터 유효성 체크
  const validateData = (data) => {
    const tempData = data.processData;
    for (let i = 0; i < tempData.length; i++) {
      const item = tempData[i];
      if(!item.register_asset_create_date) return true;
    };
    return false;
  };

  // EditRowModal 렌더링
  return (
    <ModalTemplate
      isOpen={isOpen}
      handleConfirm={editAction}
      handleCancel={() => {
        clearEditData();
        onCancel();
      }}
      title="수정하기"
      cancelText="취소"
      confirmText="수정"
      containerSX={{ width: 'auto', minWidth: '10%', maxWidth: '90%', minHeight: '10%', maxHeight: '90%' }}
      contentSX={{ flexDirection: "column", gap: 2 }}
    >
      <Typography sx={{ fontWeight: FONT_PROPS.MEDIUM, fontSize: 16, color: theme.palette.text }}>
        기계/기구명: {editData.machineData?.safe_machine_name || editData.machineData?.machine_name || "-"}
      </Typography>
      <Box 
        sx={{ 
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: '100%',
          height: '100%',
          flexWrap: "wrap",     // 넘치면 다음 줄로 
          overflow: 'hidden',   // 넘치는 부분 숨기기
          overflowY: "auto",    // 세로 스크롤
          gap: 1,
          p: 2,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: "0.375rem", 
          boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.05)'
      }}>
        {editData.processData?.map((process, index) => {
          return (
            <Box key={index} sx={{ display: 'flex', flexDirection: 'column',  width: 'auto', height: 'auto', p: 1, gap: 3 }}>
              <Typography sx={{ fontWeight: FONT_PROPS.MEDIUM, fontSize: 14}}>
                {process.process_name}
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ko">
                <DatePicker
                  label={'등록일자'}
                  value={editData?.processData[index]?.register_asset_create_date || null}
                  onChange={(e) => {
                    const updatedProcessData = [...editData.processData];
                    updatedProcessData[index] = {
                      ...updatedProcessData[index],
                      register_asset_create_date: e,
                    };
                    setEditData({
                      ...editData,
                      processData: updatedProcessData,
                    });
                  }}
                  slotProps={{
                    field: { clearable: true },
                    textField: {
                      // fullWidth: true,
                      // sx: {
                      //   marginTop: '16px',
                      //   marginBottom: '8px',
                      // },
                    },
                  }}
                />
                <DatePicker
                  label={'삭제일자'}
                  value={editData?.processData[index]?.register_asset_delete_date || null}
                  onChange={(e) => {
                    const updatedProcessData = [...editData.processData];
                    updatedProcessData[index] = {
                      ...updatedProcessData[index],
                      register_asset_delete_date: e,
                    };
                    setEditData({
                      ...editData,
                      processData: updatedProcessData,
                    });
                  }}
                  slotProps={{
                    field: { clearable: true },
                    textField: {
                      // fullWidth: true,
                      // sx: {
                      //   marginTop: '16px',
                      //   marginBottom: '8px',
                      // },
                    },
                  }}
                />
              </LocalizationProvider>
            </Box>
          );
        })}
        {editData.processData?.length === 0 &&
          <Typography sx={{ fontWeight: FONT_PROPS.MEDIUM, fontSize: 14, color: theme.palette.text.secondary }}>
            등록된 공정이 없습니다.
          </Typography>
        }
      </Box>

      {/* 유효성 검사 알람 */}
      <SnackbarAlert 
        open={validationAlert.isOpen}
        onClose={closeValidationAlert}
        message={validationAlert.message}
        severity={validationAlert.severity} // 'success' | 'error' | 'warning' | 'info'
        // anchorOrigin = {{vertical: "top", horizontal: "center"}}
        snackBarSX={{width: '80%'}}
      />  
    </ModalTemplate>
  );
}
